import { useEffect, useState } from 'react';

export type LogoTone = 'dark' | 'light';

const LUMINANCE_LIGHT_THRESHOLD = 0.55;

/**
 * Sample a logo image's average luminance (transparent pixels excluded) and
 * classify the mark as mostly-dark or mostly-light, so the logo tile can pick
 * a contrasting background: dark logo → light tile, light logo → dark glass.
 *
 * Detection needs CORS-readable pixels. The DOM <img> loads normally either
 * way — only this offscreen probe uses crossOrigin; when the storage host
 * blocks the readback we keep the 'dark' assumption (the common case), so the
 * tile stays light and the logo visible.
 */
export function useLogoTone(src?: string): LogoTone {
  const [tone, setTone] = useState<LogoTone>('dark');
  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = () => {
      if (cancelled) return;
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;
        ctx.drawImage(probe, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let weighted = 0;
        let weight = 0;
        for (let i = 0; i < data.length; i += 4) {
          const alpha = data[i + 3] / 255;
          if (alpha < 0.125) continue; // transparent / antialiased edge
          weighted += (data[i] * 0.2126 + data[i + 1] * 0.7152 + data[i + 2] * 0.0722) * alpha;
          weight += alpha;
        }
        if (weight > 0) setTone(weighted / weight / 255 > LUMINANCE_LIGHT_THRESHOLD ? 'light' : 'dark');
      } catch {
        // Tainted canvas (no CORS headers on the storage response) — keep the
        // default 'dark' assumption.
      }
    };
    probe.src = src;
    return () => { cancelled = true; };
  }, [src]);
  return tone;
}
